import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	Inject,
	Input,
	OnInit,
	Optional
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { ActivityTemplateService } from '../activity-template/activity-template.service';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { SourceTypesLookup } from '@go-modules/interfaces/source-type-lookup';
import { TitleDescription } from '@go-modules/interfaces/title-description';
import { NgxSourceMediaPanelComponent } from '@go-modules/components/sidepanels/activity-editor/source-media-panel/source-media-panel.component';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { ActivitySourceMediaModel } from 'go-modules/models/activity/activity-source-media';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import {
	LibraryDialogComponent,
	type LibraryDialogContext
} from '@go-modules/components/dialogs/library-dialog/library-dialog.component';
import { ActivityTemplateModel } from 'go-modules/models/activity-template/activity-template.schema';
import { isDismissedModalResult } from '@go-modules/utilities';
import {
	ACTIVITY_EDITOR_CONTEXT,
	ActivityEditorContext
} from '@go-modules/components/dialogs/activity-preview-editor/activity-editor-context';
import { DisabledInteractiveDirective } from '@go-modules/directives/disabled-interactive/disabled-interactive.directive';

@Component({
	selector: 'source-media-setting',
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCheckboxModule,
		MatSelectModule,
		MatIconModule,
		MatTooltipModule,
		NgxSourceMediaPanelComponent,
		DisabledInteractiveDirective
	],
	templateUrl: './source-media-setting.component.html',
	styleUrls: ['./source-media-setting.component.scss'],
	providers: [ActivityTemplateService],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SourceMediaSettingComponent implements OnInit {
	@Input() public activity: ActivityModel;
	@Input() public selectedTemplate: ActivityTemplateModel;
	@Input() public sourceMediaControl: FormControl;
	@Input() public hasSelfSelectSourceMediaControl: FormControl;
	@Input() public sourceTypeControl: FormControl<string | null>;

	public sourceTypesLookup: SourceTypesLookup;
	public sourceTypes: TitleDescription[];

	constructor (
		@Inject(mediaToken) private MediaModel,
		private cdr: ChangeDetectorRef,
		private ngxGoModalService: GoModalService,
		private destroyRef: DestroyRef,
		@Optional() @Inject(ACTIVITY_EDITOR_CONTEXT) private editorContext: ActivityEditorContext | null
	) {
		this.sourceTypesLookup = {
			INSTRUCTOR_SELECTED: {
				title: 'activity-editor-new_source-media-instructor-selected-title',
				description: 'activity-editor-new_source-media-instructor-selected-description'
			},
			PRESENTER_SELECTED: {
				title: 'activity-editor-new_source-media-presenter-selected-title',
				description: 'activity-editor-new_source-media-presenter-selected-description'
			}
		};

		this.sourceTypes = [
			this.sourceTypesLookup.INSTRUCTOR_SELECTED,
			this.sourceTypesLookup.PRESENTER_SELECTED
		];
	}

	public ngOnInit () {
		this.sourceTypeControl.setValue(this.getSourceTypeFromControls(), { emitEvent: false });
	}

	public shouldShowSourceTypeDropdown (): boolean {
		return this.selectedTemplate?.isActive?.('has_response_media') === true;
	}

	public shouldShowSourceMediaButton (): boolean {
		if (this.selectedTemplate == null) {
			return false;
		}

		return !this.selectedTemplate.isActive?.('has_response_media') &&
			this.sourceMediaControl.value == null;
	}

	public onSourceTypeChange (change: MatSelectChange): void {
		const previousSourceType = this.getSourceTypeFromControls();

		if (
			change.value === this.sourceTypesLookup.INSTRUCTOR_SELECTED.title &&
			this.isSourceMediaSelectionUnavailable()
		) {
			this.sourceTypeControl.setValue(previousSourceType);
			return;
		}

		this.sourceTypeControl.setValue(change.value);

		if (this.sourceTypeControl.value === this.sourceTypesLookup.INSTRUCTOR_SELECTED.title) {
			this.hasSelfSelectSourceMediaControl.setValue(false);

			this.openSourceMediaModal();
		}

		if (this.sourceTypeControl.value === this.sourceTypesLookup.PRESENTER_SELECTED.title) {
			this.sourceMediaControl.setValue(null);
			this.hasSelfSelectSourceMediaControl.setValue(true);
		}
	}

	public isSourceTypeUnavailable (sourceType: TitleDescription): boolean {
		return sourceType.title === this.sourceTypesLookup.INSTRUCTOR_SELECTED.title &&
			this.isSourceMediaSelectionUnavailable();
	}

	public handleOnRemoveSourceMedia () {
		this.sourceMediaControl.setValue(null);
		this.openSourceMediaModal();
	}

	public openSourceMediaModal () {
		if (this.isSourceMediaSelectionUnavailable()) {
			return;
		}

		this.ngxGoModalService.open(LibraryDialogComponent, false, {
			data: {
				filterType: [CONTENT_TYPES.MEDIA, CONTENT_TYPES.DOCUMENTS],
				mode: MODES.SELECT,
				previewable: true,
				collectionManagement: true,
				...this.getLibraryDialogContextData()
			}
		}).afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (res: unknown) => {
					if (res != null && !isDismissedModalResult(res)) {
						const media = this.MediaModel.model(res);
						const sourceMedia = ActivitySourceMediaModel.create({ media, media_id: media.media_id });

						this.updateSourceMedia(sourceMedia);

						this.sourceTypeControl.markAsDirty();
					} else {
						this.removeSourceMedia();
					}
				},
				error: () => {
					this.removeSourceMedia();
				}
			});
	}

	public handleMediaSync (event) {
		this.updateSourceMedia(event);
	}

	public getSelectedSourceType (): TitleDescription | undefined {
		const selectedKey = this.sourceTypeControl.value;
		if (selectedKey == null) return undefined;
		return this.sourceTypes.find((s) => s.title === selectedKey);
	}

	public isSourceMediaSelectionUnavailable (): boolean {
		if (this.editorContext == null) {
			return false;
		}

		return this.editorContext.group?.() == null && this.editorContext.account?.() == null;
	}

	private getLibraryDialogContextData (): { context: LibraryDialogContext } | Record<string, never> {
		if (this.editorContext == null) {
			return {};
		}

		return {
			context: {
				group: this.editorContext.group?.() ?? null,
				account: this.editorContext.account?.() ?? null,
				org: this.editorContext.org?.() ?? null,
				license: this.editorContext.license?.() ?? null,
				orgSettings: this.editorContext.orgSettings?.() ?? null
			}
		};
	}

	private removeSourceMedia (): void {
		this.sourceMediaControl.setValue(null);
		this.sourceTypeControl.setValue(null);
	}

	private getSourceTypeFromControls (): string | null {
		if (this.hasSelfSelectSourceMediaControl.value) {
			return this.sourceTypesLookup.PRESENTER_SELECTED.title;
		}

		if (this.sourceMediaControl.value != null) {
			return this.sourceTypesLookup.INSTRUCTOR_SELECTED.title;
		}

		return null;
	}

	private updateSourceMedia (sourceMedia): void {
		this.sourceMediaControl.setValue(sourceMedia);
		this.cdr.markForCheck();
	}
}
